import { handleApi } from "../utils/authentication";
import { store } from "../redux/store";

export const createCheckoutSession = (payload) =>
  handleApi(
    "stripe/create-checkout-session",
    "POST",
    { "Content-Type": "application/json" },
    JSON.stringify(payload),
  );

export const verifyCheckoutSession = (sessionId) =>
  handleApi(
    "stripe/checkout-success",
    "POST",
    { "Content-Type": "application/json" },
    JSON.stringify({ session_id: sessionId }),
  );

export const getSubscriptions = () => {
  const { user } = store.getState();
  return handleApi(`stripe/subscriptions?user_id=${user?.user_id}`, "GET", {
    "Content-Type": "application/json",
  });
};

// Plans
export const getPlans = () =>
  handleApi("stripe/products?active=1", "GET", {
    "Content-Type": "application/json",
  });

export const getPlanById = (id) =>
  handleApi(`stripe/products/${id}`, "GET", {
    "Content-Type": "application/json",
  });

const emptyCardsResponse = () => ({
  success: true,
  cards: [],
  total: 0,
  customer_id: null,
});

const getBillingEmail = () => {
  const email = store.getState()?.user?.email;
  return typeof email === "string" ? email.trim() : "";
};

// Cards
export const getCards = () => {
  const email = getBillingEmail();
  if (!email) {
    return Promise.resolve(emptyCardsResponse());
  }

  return handleApi(
    `stripe/cards?email=${encodeURIComponent(email)}`,
    "GET",
    { "Content-Type": "application/json" },
  );
};

export const addCard = (payload = {}) => {
  const email = getBillingEmail();
  if (!email) {
    return Promise.resolve({
      success: false,
      error: "Your account email is missing. Please sign in again.",
    });
  }

  const paymentMethodId = payload?.payment_method_id;
  if (!paymentMethodId) {
    return Promise.resolve({
      success: false,
      error: "Payment method is required.",
    });
  }

  return handleApi(
    "stripe/cards/add",
    "POST",
    { "Content-Type": "application/json" },
    JSON.stringify({ email, payment_method_id: paymentMethodId }),
  );
};

export const deleteCard = (id) => {
  const paymentMethodId =
    typeof id === "string" || typeof id === "number" ? String(id).trim() : "";

  if (!paymentMethodId) {
    return Promise.resolve({
      success: false,
      error: "Payment method is required.",
    });
  }

  return handleApi(`stripe/cards/${encodeURIComponent(paymentMethodId)}`, "DELETE", {
    "Content-Type": "application/json",
  });
};

// Subscription
export const subscribeToPlan = (payload = {}) => {
  const email = typeof payload.email === "string" ? payload.email.trim() : "";
  const priceId =
    typeof payload.price_id === "string" ? payload.price_id.trim() : "";
  const paymentMethodId =
    typeof payload.payment_method_id === "string"
      ? payload.payment_method_id.trim()
      : String(payload.payment_method_id ?? "").trim();

  if (!email || !payload.plan_id || !priceId || !paymentMethodId) {
    return Promise.resolve({
      success: false,
      error: "Complete all subscription details before continuing.",
    });
  }

  return handleApi(
    "stripe/subscribe",
    "POST",
    { "Content-Type": "application/json" },
    JSON.stringify({
      ...payload,
      email,
      price_id: priceId,
      payment_method_id: paymentMethodId,
      name:
        typeof payload.name === "string" && payload.name.trim()
          ? payload.name.trim()
          : email,
    }),
  );
};

// Invoices — the API binds them to the authenticated account's email
export const getInvoices = (limit = 10) => {
  const email = getBillingEmail();
  if (!email) {
    return Promise.resolve({ success: true, invoices: [], total: 0 });
  }

  return handleApi(
    `stripe/invoices?email=${encodeURIComponent(email)}&limit=${limit}`,
    "GET",
    { "Content-Type": "application/json" },
  );
};

export const setAutoRenew = (subscriptionId, autoRenew) => {
  const id = String(subscriptionId ?? "").trim();

  if (!id) {
    return Promise.resolve({
      success: false,
      error: "Subscription is required.",
    });
  }

  return handleApi(
    `stripe/subscriptions/${encodeURIComponent(id)}/auto-renew`,
    "POST",
    { "Content-Type": "application/json" },
    JSON.stringify({ auto_renew: Boolean(autoRenew) }),
  );
};

export const setDefaultCard = (paymentMethodId) => {
  const email = getBillingEmail();
  const id = String(paymentMethodId ?? "").trim();

  if (!email || !id) {
    return Promise.resolve({
      success: false,
      error: "Payment method is required.",
    });
  }

  return handleApi(
    "stripe/cards/set-default",
    "POST",
    { "Content-Type": "application/json" },
    JSON.stringify({ email, payment_method_id: id }),
  );
};

export const cancelSubscription = (subscriptionId) => {
  const id = String(subscriptionId ?? "").trim();

  if (!id) {
    return Promise.resolve({
      success: false,
      error: "Subscription is required.",
    });
  }

  return handleApi(
    `stripe/cancel-subscription/${encodeURIComponent(id)}`,
    "POST",
    { "Content-Type": "application/json" },
  );
};

// Receipt download — returns a Blob so the caller can trigger a file save
export const downloadSubscriptionReceipt = async (subscriptionId) => {
  const subscriptionKey =
    typeof subscriptionId === "string" || typeof subscriptionId === "number"
      ? String(subscriptionId).trim()
      : "";

  if (!subscriptionKey) {
    throw new Error("Subscription id is required.");
  }

  const { user } = store.getState();
  const headers = { "Content-Type": "application/json" };
  if (user?.token) {
    headers["token"] = user.token;
  }

  const response = await fetch(
    `${process.env.REACT_APP_API_URL}stripe/subscriptions/${encodeURIComponent(subscriptionKey)}/receipt/download`,
    { method: "GET", headers },
  );

  if (!response.ok) {
    let message = `Request failed with status ${response.status}`;
    try {
      const errBody = await response.json();
      message = errBody?.message || errBody?.error || message;
    } catch (_) {
      // ignore parse errors
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const contentDisposition = response.headers.get("content-disposition") ?? "";
  const match = contentDisposition.match(/filename[^;=\n]*=["']?([^"';\n]+)/i);
  const filename = match?.[1]?.trim() || `receipt-${subscriptionKey}.pdf`;

  return { blob, filename };
};
